'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Link from 'next/link';
import {
  Crown,
  TrendingUp,
  Users,
  PieChart,
  Globe,
  ChevronDown,
  Star,
  Menu,
  X,
  Zap,
  BarChart3,
  MousePointerClick,
  Activity,
  ArrowRight,
  Shield,
  Clock,
  Award,
} from 'lucide-react';

const tickers = [
  { symbol: 'EUR/USD', price: '1.0847', change: '+0.12%' },
  { symbol: 'GBP/USD', price: '1.2634', change: '+0.08%' },
  { symbol: 'USD/JPY', price: '149.82', change: '-0.15%' },
  { symbol: 'BTC/USD', price: '87,245', change: '+2.34%' },
  { symbol: 'ETH/USD', price: '3,421', change: '+1.87%' },
  { symbol: 'Gold', price: '2,987.50', change: '+0.45%' },
  { symbol: 'S&P 500', price: '5,842.3', change: '+0.32%' },
  { symbol: 'DAX', price: '18,472', change: '-0.21%' },
  { symbol: 'USD/CHF', price: '0.8823', change: '+0.05%' },
  { symbol: 'AUD/USD', price: '0.6543', change: '-0.18%' },
  { symbol: 'Crude Oil', price: '78.34', change: '+0.67%' },
  { symbol: 'SOL/USD', price: '142.80', change: '+3.12%' },
];

const stats = [
  { value: '10+', label: 'Years of Experience' },
  { value: '25K+', label: 'Satisfied Customers' },
  { value: '180+', label: 'Trading Instruments' },
  { value: '<12ms', label: 'Execution Speed' },
];

const features = [
  {
    icon: 'TrendingUp',
    title: 'High Profit Potential',
    description:
      'Leverage market movements across multiple asset classes to maximize your portfolio returns with professional-grade tools.',
  },
  {
    icon: 'Users',
    title: 'Accessibility for All',
    description:
      "Whether you're a beginner or a seasoned professional, our platform adapts to your experience level with intuitive tools.",
  },
  {
    icon: 'PieChart',
    title: 'Portfolio Diversification',
    description:
      'Spread your investments across Forex, Stocks, Indices, Commodities and Crypto — all from a single account.',
  },
  {
    icon: 'Globe',
    title: 'Global Liquidity',
    description:
      'Access deep institutional liquidity pools ensuring tight spreads and fast execution across all market conditions.',
  },
];

const markets = [
  {
    name: 'Forex',
    pairs: '60+ pairs',
    spreads: 'From 0.0 pips',
    leverage: 'Up to 1:500',
    description: 'Major, minor and exotic currency pairs with deep liquidity',
    examples: ['EUR/USD', 'GBP/USD', 'USD/JPY', 'AUD/CAD'],
    color: '#7C3AED',
  },
  {
    name: 'Stocks',
    pairs: '500+ CFDs',
    spreads: 'From 0.1%',
    leverage: 'Up to 1:20',
    description: 'Trade global equities from NYSE, NASDAQ, LSE, and more',
    examples: ['Apple', 'Tesla', 'Amazon', 'NVIDIA'],
    color: '#6366F1',
  },
  {
    name: 'Indices',
    pairs: '20+ indices',
    spreads: 'From 0.4 pts',
    leverage: 'Up to 1:200',
    description: "Access the world's major stock indices in a single click",
    examples: ['S&P 500', 'DAX 40', 'FTSE 100', 'Nikkei 225'],
    color: '#10B981',
  },
  {
    name: 'Commodities',
    pairs: '15+ instruments',
    spreads: 'From 0.03',
    leverage: 'Up to 1:100',
    description: 'Precious metals, energy and soft commodities',
    examples: ['Gold', 'Silver', 'Crude Oil', 'Natural Gas'],
    color: '#F59E0B',
  },
  {
    name: 'Crypto',
    pairs: '30+ pairs',
    spreads: 'From 0.1%',
    leverage: 'Up to 1:10',
    description: 'Trade the top cryptocurrencies 24/7 with competitive spreads',
    examples: ['BTC/USD', 'ETH/USD', 'SOL/USD', 'XRP/USD'],
    color: '#A78BFA',
  },
];

const accounts = [
  {
    name: 'Standard',
    minDeposit: '$10,000',
    popular: false,
    features: [
      '180+ trading instruments',
      'Personal account manager',
      'Daily market analysis',
      'Email & chat support',
      'Demo account included',
      'Mobile trading app',
    ],
  },
  {
    name: 'Gold',
    minDeposit: '$100,000',
    popular: true,
    features: [
      'Everything in Standard',
      'Raw spreads access',
      'VIP event invitations',
      'Tax optimization guidance',
      'Priority withdrawals',
      'One-on-one coaching',
    ],
  },
  {
    name: 'VIP',
    minDeposit: '$250,000',
    popular: false,
    features: [
      'Everything in Gold',
      'Zero spread on majors',
      'Institutional liquidity',
      'Custom API access',
      'Dedicated trading desk',
      'Concierge support 24/7',
    ],
  },
];

const testimonials = [
  {
    name: 'Alexander Braun',
    role: 'Professional Trader',
    text: "RoyalMarkets execution speed is on par with institutional platforms I've used. The raw spreads on EUR/USD are consistently competitive, even during high-volatility events.",
    rating: 5,
  },
  {
    name: 'Marie Johansson',
    role: 'Portfolio Manager',
    text: 'The multi-asset access from a single account is what sold me. I manage forex, commodities, and equity CFDs seamlessly — the charting tools are genuinely professional-grade.',
    rating: 5,
  },
  {
    name: 'David Chen',
    role: 'Algorithmic Trader',
    text: "The API is well-documented and the latency is excellent. I've migrated three algo strategies to RoyalMarkets and the fill quality improvement was immediately measurable.",
    rating: 5,
  },
  {
    name: 'Sofia Petrova',
    role: 'Risk Analyst',
    text: 'Impressed by the transparency — no hidden fees, clear margin requirements, and the risk tools are built into the platform rather than afterthoughts.',
    rating: 5,
  },
];

const faqs = [
  {
    q: 'What is RoyalMarkets?',
    a: 'RoyalMarkets is a regulated online trading platform providing access to Forex, Stocks, Indices, Commodities and Crypto CFDs. We combine institutional-grade technology with competitive pricing and dedicated client support.',
  },
  {
    q: 'How do I open a trading account?',
    a: "Click 'Open Account', complete the registration form with your personal details, verify your identity (KYC), and fund your account. The entire process takes less than 10 minutes and you can start trading immediately.",
  },
  {
    q: 'What is the minimum deposit?',
    a: 'Our Standard account requires a minimum deposit of $10,000. Higher-tier accounts — Gold ($100,000) and VIP ($250,000) — offer progressively tighter spreads, dedicated support, and premium services.',
  },
  {
    q: 'Is my money safe with RoyalMarkets?',
    a: 'Yes. Client funds are held in segregated accounts at top-tier European banks, completely separate from company operational funds. We also offer negative balance protection and are regulated by financial authorities.',
  },
  {
    q: 'What trading platforms do you offer?',
    a: 'We offer our proprietary web-based platform, desktop applications for Windows and Mac, and fully-featured mobile apps for iOS and Android. All platforms include advanced charting, one-click trading, and real-time market data.',
  },
  {
    q: 'How can I withdraw my funds?',
    a: 'Withdrawals can be made via bank wire transfer, credit/debit card, or cryptocurrency. Requests are processed within 1-2 business days. There are no fees on standard withdrawals.',
  },
  {
    q: 'What spreads do you offer?',
    a: 'Spreads vary by account tier. Standard accounts start from competitive market spreads, while VIP accounts offer raw spreads from 0.0 pips with institutional liquidity. Actual spreads depend on market conditions.',
  },
  {
    q: 'Do you offer educational resources?',
    a: 'Yes. We provide trading webinars, video tutorials, market analysis, economic calendar, and dedicated account managers who can guide you through your trading journey.',
  },
];

const footerLinks: Record<string, { label: string; href: string }[]> = {
  'Forex Trading': [
    { label: 'What is Forex', href: '#' },
    { label: 'How to Trade Forex', href: '#' },
    { label: 'EUR/USD', href: '#' },
    { label: 'GBP/USD', href: '#' },
    { label: 'USD/JPY', href: '#' },
    { label: 'Forex Spreads', href: '#' },
    { label: 'Forex Strategies', href: '#' },
  ],
  'CFD Trading': [
    { label: 'What are CFDs', href: '#' },
    { label: 'Stock CFDs', href: '#' },
    { label: 'Index CFDs', href: '#' },
    { label: 'Commodity CFDs', href: '#' },
    { label: 'Gold Trading', href: '#' },
    { label: 'Oil Trading', href: '#' },
  ],
  Cryptocurrencies: [
    { label: 'Bitcoin Trading', href: '#' },
    { label: 'Ethereum Trading', href: '#' },
    { label: 'Crypto CFDs', href: '#' },
    { label: 'Crypto Spreads', href: '#' },
    { label: 'Crypto Market Hours', href: '#' },
  ],
  'Trading Info': [
    { label: 'Account Types', href: '#' },
    { label: 'Deposits & Withdrawals', href: '#' },
    { label: 'Leverage & Margin', href: '#' },
    { label: 'Trading Conditions', href: '#' },
    { label: 'Economic Calendar', href: '#' },
    { label: 'Market Analysis', href: '#' },
  ],
  About: [
    { label: 'About RoyalMarkets', href: '#' },
    { label: 'Regulation & Licences', href: '#' },
    { label: 'Security of Funds', href: '#' },
    { label: 'Contact Us', href: '#' },
    { label: 'Careers', href: '#' },
    { label: 'Legal Documents', href: '#' },
  ],
};

const featureIcons: Record<string, React.ReactNode> = {
  TrendingUp: <TrendingUp className="w-6 h-6" />,
  Users: <Users className="w-6 h-6" />,
  PieChart: <PieChart className="w-6 h-6" />,
  Globe: <Globe className="w-6 h-6" />,
};

const navLinks = ['Markets', 'Accounts', 'Platform', 'About', 'Contact'];

export default function RoyalMarketsPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* ── Back to gallery ── */}
      <a
        href="/templates/"
        className="fixed bottom-5 left-1/2 -translate-x-1/2 md:bottom-auto md:top-4 md:left-4 md:translate-x-0 z-[200] flex items-center gap-1.5 bg-black/70 hover:bg-black/90 backdrop-blur-sm text-white text-xs font-semibold px-4 py-2 rounded-full transition-all duration-200 shadow-xl border border-white/10"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M19 12H5M12 5l-7 7 7 7" />
        </svg>
        <span className="hidden sm:inline">All Templates</span>
        <span className="sm:hidden">Templates</span>
      </a>

      {/* ========== HEADER (LIGHT, FIXED) ========== */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-white'
        }`}
      >
        <div className="site-container flex items-center justify-between h-16 px-4 lg:px-8">
          <Link href="#" className="flex items-center gap-2">
            <Crown className="w-7 h-7 text-[#7C3AED]" />
            <span className="text-xl font-bold bg-gradient-to-r from-[#7C3AED] to-[#6366F1] bg-clip-text text-transparent">
              RoyalMarkets
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link}
                href="#"
                className="text-sm font-medium text-[#1E1B4B]/70 hover:text-[#7C3AED] transition-colors"
              >
                {link}
              </Link>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <Link
              href="#"
              className="text-sm font-medium text-[#1E1B4B] hover:text-[#7C3AED] transition-colors"
            >
              Log In
            </Link>
            <Link
              href="#"
              className="px-5 py-2 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-[#7C3AED] to-[#6366F1] hover:opacity-90 transition-opacity"
            >
              Open Account
            </Link>
          </div>
          <button className="md:hidden text-[#1E1B4B]" onClick={() => setMobileMenu(!mobileMenu)}>
            {mobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {/* Mobile menu */}
        <AnimatePresence>
          {mobileMenu && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white border-t border-[#E9E5FF] overflow-hidden"
            >
              <div className="px-4 py-4 flex flex-col gap-3">
                {navLinks.map((link) => (
                  <Link
                    key={link}
                    href="#"
                    className="text-sm font-medium text-[#1E1B4B]/70 hover:text-[#7C3AED] py-2"
                  >
                    {link}
                  </Link>
                ))}
                <div className="flex gap-3 pt-2">
                  <Link href="#" className="text-sm font-medium text-[#1E1B4B]">
                    Log In
                  </Link>
                  <Link
                    href="#"
                    className="px-5 py-2 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-[#7C3AED] to-[#6366F1]"
                  >
                    Open Account
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* ========== TICKER BAR (VIOLET GRADIENT) ========== */}
      <div className="mt-16 bg-gradient-to-r from-[#7C3AED] to-[#6366F1] overflow-hidden">
        <div className="ticker-animate flex items-center gap-8 py-2.5 whitespace-nowrap">
          {[...tickers, ...tickers].map((t, i) => (
            <span key={i} className="flex items-center gap-2 text-sm text-white/90">
              <span className="font-semibold text-white">{t.symbol}</span>
              <span>{t.price}</span>
              <span className={t.change.startsWith('+') ? 'text-[#A7F3D0]' : 'text-[#FCA5A5]'}>
                {t.change}
              </span>
            </span>
          ))}
        </div>
      </div>

      {/* ========== HERO (LIGHT) ========== */}
      <section className="site-section bg-white">
        <div className="site-container px-4 lg:px-8 py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block px-4 py-1.5 text-xs font-semibold text-[#7C3AED] bg-[#F5F3FF] rounded-full border border-[#E9E5FF] mb-6">
                Trusted by 25,000+ traders worldwide
              </span>
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-[#1E1B4B] leading-tight mb-6">
                Trade with{' '}
                <span className="bg-gradient-to-r from-[#7C3AED] to-[#6366F1] bg-clip-text text-transparent">
                  RoyalMarkets
                </span>
                <br />
                Confidence & Precision
              </h1>
              <p className="text-lg text-[#1E1B4B]/60 mb-8 max-w-lg">
                Access 180+ instruments across Forex, Stocks, Indices, Commodities and Crypto.
                Institutional-grade execution with spreads from 0.0 pips.
              </p>
              <div className="flex flex-wrap gap-4 mb-8">
                <Link
                  href="#"
                  className="px-8 py-3.5 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-[#7C3AED] to-[#6366F1] hover:opacity-90 transition-opacity flex items-center gap-2"
                >
                  Start Trading <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  href="#"
                  className="px-8 py-3.5 text-sm font-semibold text-[#7C3AED] rounded-lg border-2 border-[#7C3AED] hover:bg-[#F5F3FF] transition-colors"
                >
                  Try Demo Account
                </Link>
              </div>
              <div className="flex items-center gap-6 text-sm text-[#1E1B4B]/50">
                <span className="flex items-center gap-1.5">
                  <Shield className="w-4 h-4 text-[#7C3AED]" /> Regulated Broker
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-[#7C3AED]" /> Instant Execution
                </span>
                <span className="flex items-center gap-1.5">
                  <Award className="w-4 h-4 text-[#7C3AED]" /> Award Winning
                </span>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="rounded-2xl bg-gradient-to-br from-[#7C3AED]/10 to-[#6366F1]/10 border border-[#7C3AED]/30 p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-semibold text-[#1E1B4B]">EUR/USD</span>
                  <span className="text-sm font-semibold text-[#10B981]">+0.12%</span>
                </div>
                <svg viewBox="0 0 400 200" className="w-full">
                  <defs>
                    <linearGradient id="royalGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#7C3AED" stopOpacity="0.3" />
                      <stop offset="100%" stopColor="#7C3AED" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M0,150 Q50,140 100,110 T200,80 T300,90 T400,40"
                    fill="none"
                    stroke="#7C3AED"
                    strokeWidth="2.5"
                  />
                  <path
                    d="M0,150 Q50,140 100,110 T200,80 T300,90 T400,40 L400,200 L0,200 Z"
                    fill="url(#royalGrad)"
                  />
                </svg>
                <div className="flex items-center justify-between mt-4 text-xs text-[#1E1B4B]/50">
                  <span>09:00</span>
                  <span>12:00</span>
                  <span>15:00</span>
                  <span>18:00</span>
                  <span>21:00</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ========== STATS (LIGHT SURFACE) ========== */}
      <section className="site-section bg-[#F5F3FF]">
        <div className="site-container px-4 lg:px-8 py-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-[#7C3AED] to-[#6366F1] bg-clip-text text-transparent mb-2">
                  {s.value}
                </div>
                <div className="text-sm text-[#1E1B4B]/60">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FEATURES (DARK) ========== */}
      <section className="site-section bg-[#0F0B1A]">
        <div className="site-container px-4 lg:px-8 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-14"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-[#F5F3FF] mb-4">
              Why Choose RoyalMarkets
            </h2>
            <p className="text-[#F5F3FF]/60 max-w-2xl mx-auto">
              Industry-leading technology, competitive pricing, and dedicated support designed for
              serious traders.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6">
            {features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="rounded-2xl p-8 bg-[#1A1625] border border-[#7C3AED]/20 hover:border-[#7C3AED]/40 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-[#7C3AED]/20 flex items-center justify-center text-[#A78BFA] mb-5">
                  {featureIcons[f.icon]}
                </div>
                <h3 className="text-xl font-semibold text-[#F5F3FF] mb-3">{f.title}</h3>
                <p className="text-[#F5F3FF]/60 leading-relaxed">{f.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== MARKETS (LIGHT) ========== */}
      <section className="site-section bg-white">
        <div className="site-container px-4 lg:px-8 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-14"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1E1B4B] mb-4">
              Explore Our Markets
            </h2>
            <p className="text-[#1E1B4B]/60 max-w-2xl mx-auto">
              Trade across diverse asset classes with competitive spreads and fast execution.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {markets.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="rounded-2xl p-8 bg-white border border-[#E9E5FF] shadow-sm hover:shadow-md transition-shadow relative overflow-hidden"
              >
                <div
                  className="absolute top-0 left-0 right-0 h-1"
                  style={{ backgroundColor: m.color }}
                />
                <h3 className="text-xl font-semibold text-[#1E1B4B] mb-2">{m.name}</h3>
                <p className="text-sm text-[#1E1B4B]/60 mb-4">{m.description}</p>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#1E1B4B]/50">Instruments</span>
                    <span className="font-medium text-[#1E1B4B]">{m.pairs}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#1E1B4B]/50">Spreads</span>
                    <span className="font-medium text-[#1E1B4B]">{m.spreads}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#1E1B4B]/50">Leverage</span>
                    <span className="font-medium text-[#1E1B4B]">{m.leverage}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {m.examples.map((ex) => (
                    <span
                      key={ex}
                      className="px-2.5 py-1 text-xs rounded-md bg-[#F5F3FF] text-[#7C3AED] font-medium"
                    >
                      {ex}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== PLATFORM (DARK) ========== */}
      <section className="site-section bg-[#0F0B1A]">
        <div className="site-container px-4 lg:px-8 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-14"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-[#F5F3FF] mb-4">
              Powerful Trading Platform
            </h2>
            <p className="text-[#F5F3FF]/60 max-w-2xl mx-auto">Advanced tools for every trader</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              {
                icon: <BarChart3 className="w-7 h-7" />,
                title: 'Advanced Charts',
                desc: 'Professional-grade charting with 100+ indicators and drawing tools',
              },
              {
                icon: <MousePointerClick className="w-7 h-7" />,
                title: 'One-Click Trading',
                desc: 'Execute trades instantly with single-click order placement',
              },
              {
                icon: <Activity className="w-7 h-7" />,
                title: 'Real-Time Data',
                desc: 'Live streaming prices and market depth across all instruments',
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="text-center"
              >
                <div className="w-14 h-14 rounded-xl bg-[#7C3AED]/20 flex items-center justify-center text-[#A78BFA] mx-auto mb-4">
                  {item.icon}
                </div>
                <h3 className="text-lg font-semibold text-[#F5F3FF] mb-2">{item.title}</h3>
                <p className="text-sm text-[#F5F3FF]/60">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== ACCOUNTS (LIGHT) ========== */}
      <section className="site-section bg-white">
        <div className="site-container px-4 lg:px-8 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-14"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1E1B4B] mb-4">
              Choose Your Account
            </h2>
            <p className="text-[#1E1B4B]/60 max-w-2xl mx-auto">
              Three tiers designed to match your trading volume and goals.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {accounts.map((acc, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`rounded-2xl p-8 bg-white border shadow-sm relative ${
                  acc.popular
                    ? 'border-[#7C3AED] shadow-[#7C3AED]/10 shadow-lg scale-[1.02]'
                    : 'border-[#E9E5FF]'
                }`}
              >
                {acc.popular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 text-xs font-semibold text-white rounded-full bg-gradient-to-r from-[#7C3AED] to-[#6366F1]">
                    Most Popular
                  </div>
                )}
                <h3 className="text-xl font-bold text-[#1E1B4B] mb-1">{acc.name}</h3>
                <div className="text-3xl font-bold bg-gradient-to-r from-[#7C3AED] to-[#6366F1] bg-clip-text text-transparent mb-1">
                  {acc.minDeposit}
                </div>
                <p className="text-xs text-[#1E1B4B]/50 mb-6">Minimum deposit</p>
                <ul className="space-y-3 mb-8">
                  {acc.features.map((feat, fi) => (
                    <li key={fi} className="flex items-start gap-2 text-sm text-[#1E1B4B]/70">
                      <Zap className="w-4 h-4 text-[#7C3AED] mt-0.5 shrink-0" />
                      {feat}
                    </li>
                  ))}
                </ul>
                <Link
                  href="#"
                  className={`block text-center py-3 rounded-lg text-sm font-semibold transition-opacity ${
                    acc.popular
                      ? 'bg-gradient-to-r from-[#7C3AED] to-[#6366F1] text-white hover:opacity-90'
                      : 'border-2 border-[#7C3AED] text-[#7C3AED] hover:bg-[#F5F3FF]'
                  }`}
                >
                  Get Started
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIALS (DARK) ========== */}
      <section className="site-section bg-[#0F0B1A]">
        <div className="site-container px-4 lg:px-8 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-14"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-[#F5F3FF] mb-4">What Traders Say</h2>
            <p className="text-[#F5F3FF]/60 max-w-2xl mx-auto">
              Hear from professionals who trust RoyalMarkets for their trading.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="rounded-2xl p-8 bg-[#1A1625] border border-[#7C3AED]/20 hover:border-[#7C3AED]/40 transition-colors"
              >
                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: t.rating }).map((_, si) => (
                    <Star key={si} className="w-4 h-4 fill-[#F59E0B] text-[#F59E0B]" />
                  ))}
                </div>
                <p className="text-[#F5F3FF]/80 mb-6 leading-relaxed">&ldquo;{t.text}&rdquo;</p>
                <div>
                  <div className="font-semibold text-[#F5F3FF]">{t.name}</div>
                  <div className="text-sm text-[#A78BFA]">{t.role}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FAQ (LIGHT) ========== */}
      <section className="site-section bg-white">
        <div className="site-container px-4 lg:px-8 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-14"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1E1B4B] mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-[#1E1B4B]/60 max-w-2xl mx-auto">
              Everything you need to know about trading with RoyalMarkets.
            </p>
          </motion.div>
          <div className="max-w-3xl mx-auto space-y-3">
            {faqs.map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="rounded-xl border border-[#E9E5FF] overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="flex items-center justify-between w-full p-5 text-left"
                >
                  <span className="font-semibold text-[#1E1B4B] pr-4">{faq.q}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-[#7C3AED] shrink-0 transition-transform duration-300 ${
                      openFaq === i ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 pb-5 text-[#1E1B4B]/60 leading-relaxed">{faq.a}</div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== CTA (DARK) ========== */}
      <section className="site-section bg-[#0F0B1A]">
        <div className="site-container px-4 lg:px-8 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="rounded-2xl bg-gradient-to-r from-[#7C3AED] to-[#6366F1] p-10 lg:p-16 text-center"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Ready to Start Trading?
            </h2>
            <p className="text-white/80 max-w-xl mx-auto mb-8">
              Join 25,000+ traders who trust RoyalMarkets. Open your account today and experience
              institutional-grade trading.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                href="#"
                className="px-8 py-3.5 text-sm font-semibold rounded-lg bg-white text-[#7C3AED] hover:bg-white/90 transition-colors"
              >
                Open Live Account
              </Link>
              <Link
                href="#"
                className="px-8 py-3.5 text-sm font-semibold rounded-lg border-2 border-white text-white hover:bg-white/10 transition-colors"
              >
                Try Free Demo
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ========== FOOTER (DARK) ========== */}
      <footer className="bg-[#0F0B1A]">
        <div className="site-container px-4 lg:px-8">
          <div className="border-t border-[#7C3AED]/30 pt-12 pb-8">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 mb-12">
              {Object.entries(footerLinks).map(([category, links]) => (
                <div key={category}>
                  <h4 className="text-sm font-semibold text-[#F5F3FF] mb-4">{category}</h4>
                  <ul className="space-y-2.5">
                    {links.map((link) => (
                      <li key={link.label}>
                        <Link
                          href={link.href}
                          className="text-sm text-[#F5F3FF]/50 hover:text-[#A78BFA] transition-colors"
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Social icons + bottom */}
            <div className="border-t border-[#7C3AED]/20 pt-8 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-2">
                <Crown className="w-6 h-6 text-[#7C3AED]" />
                <span className="text-lg font-bold bg-gradient-to-r from-[#7C3AED] to-[#6366F1] bg-clip-text text-transparent">
                  RoyalMarkets
                </span>
              </div>
              <div className="flex items-center gap-4">
                {/* Twitter/X */}
                <a href="#" className="text-[#A78BFA] hover:text-[#7C3AED] transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
                {/* LinkedIn */}
                <a href="#" className="text-[#A78BFA] hover:text-[#7C3AED] transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
                {/* Telegram */}
                <a href="#" className="text-[#A78BFA] hover:text-[#7C3AED] transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                  </svg>
                </a>
                {/* Instagram */}
                <a href="#" className="text-[#A78BFA] hover:text-[#7C3AED] transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 100 12.324 6.162 6.162 0 100-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 11-2.882 0 1.441 1.441 0 012.882 0z" />
                  </svg>
                </a>
                {/* YouTube */}
                <a href="#" className="text-[#A78BFA] hover:text-[#7C3AED] transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                </a>
              </div>
              <p className="text-xs text-[#F5F3FF]/30 text-center md:text-right max-w-lg">
                Risk Warning: Trading CFDs and Forex involves significant risk and may not be
                suitable for all investors. You may lose more than your initial investment.
                RoyalMarkets is a trading name. Past performance is not indicative of future
                results.
              </p>
            </div>

            <div className="border-t border-[#7C3AED]/10 mt-8 pt-6 text-center">
              <p className="text-xs text-[#F5F3FF]/20">
                &copy; {new Date().getFullYear()} RoyalMarkets. All rights reserved. |{' '}
                <Link href="#" className="hover:text-[#A78BFA] transition-colors">
                  Privacy Policy
                </Link>{' '}
                |{' '}
                <Link href="#" className="hover:text-[#A78BFA] transition-colors">
                  Terms of Service
                </Link>{' '}
                |{' '}
                <Link href="#" className="hover:text-[#A78BFA] transition-colors">
                  Cookie Policy
                </Link>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
